'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import CinematicScene from './CinematicScene';
import EmailLoginInline from '@/components/EmailLoginInline';
import { RUSTORE_APP_URL, ruStoreOffer, type RuStoreOffer } from '@shared/services/appStores';

/**
 * SCENE 11: Финал. Cinematic-фон 3.webp + две CTA-кнопки (TG + VK).
 * Использует общий CinematicScene-каркас с раздельным mobile/desktop layout.
 * Вне iOS и установленного приложения дополнительно показывается бейдж
 * «Скачайте в RuStore» — вторичный CTA, не конкурирует с основными кнопками;
 * на десктопе рядом с бейджем QR-код на страницу стора (навёл камеру телефона —
 * попал в RuStore).
 */
export default function Scene11Final({ botUrl, vkUrl }: { botUrl: string; vkUrl: string }) {
  const [ruStore, setRuStore] = useState<RuStoreOffer>(null);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    setRuStore(ruStoreOffer(navigator.userAgent, standalone));
  }, []);

  return (
    <CinematicScene imageAlt="Маскот и человек на закатном холме — приглашение к старту" imageSrc="/landing/110526/3.webp">
      <h2
        className="font-display font-bold text-white tracking-[-0.02em] leading-[0.96] mb-6"
        style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)', textShadow: '0 4px 24px rgba(0,0,0,0.4)' }}
      >
        Быстрый<br />
        <span className="text-[#f5a85a]">старт.</span>
      </h2>
      <p
        className="font-body text-white/85 text-base sm:text-lg leading-relaxed mb-9 max-w-md"
        style={{ textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}
      >
        Запусти бота и перешли несколько своих заметок. Начать можно из Telegram или VK.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <Link
          href={botUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
          style={{ boxShadow: '0 16px 40px -8px rgba(183,16,42,0.55)' }}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-1.97 9.289c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L8.48 14.49l-2.95-.924c-.64-.203-.654-.64.136-.95l11.52-4.44c.535-.194 1.003.13.376.072z"/>
          </svg>
          Открыть в Telegram
        </Link>
        <Link
          href={vkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-2xl bg-[#0077FF] hover:bg-[#0066DD] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
          style={{ boxShadow: '0 16px 40px -8px rgba(0,119,255,0.55)' }}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.077 17c-6.237 0-9.808-4.274-9.957-11.377H5.06c.105 5.213 2.507 7.418 4.407 7.872V5.623h2.72v4.494c1.876-.202 3.848-2.34 4.515-4.494h2.72c-.51 2.658-2.65 4.796-4.17 5.633 1.52.685 3.93 2.571 4.853 5.744h-2.995c-.72-2.24-2.51-3.971-4.923-4.208v4.208h-.11z"/>
          </svg>
          Открыть в VK
        </Link>
      </div>

      {ruStore && (
        <div className="mt-3 inline-flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl bg-black/70 border border-white/20 px-5 py-3">
          <a
            href={RUSTORE_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 hover:opacity-85 active:scale-[0.98] transition-all cursor-pointer"
          >
            <img src="/rustore-logo.svg" alt="RuStore" className="w-7 h-7" />
            <span className="flex flex-col items-start leading-tight">
              <span className="font-body text-white/70 text-[11px]">Скачайте приложение в</span>
              <span className="font-body text-white font-semibold text-base -mt-0.5">RuStore</span>
            </span>
          </a>
          {ruStore === 'desktop' && (
            <div className="flex items-center gap-2.5 border-l border-white/15 pl-4">
              <div className="bg-white rounded-lg p-1 shrink-0">
                <img src="/rustore-qr.svg" alt="QR-код: Favbox в RuStore" className="w-24 h-24" />
              </div>
              <span className="font-body text-white/60 text-xs leading-snug max-w-[110px]">
                Наведите камеру телефона
              </span>
            </div>
          )}
        </div>
      )}

      <p className="font-mono text-white/60 text-xs uppercase tracking-widest mt-6">
        бесплатно · запуск за секунду
      </p>

      <EmailLoginInline onPhoto />
    </CinematicScene>
  );
}
